import type { RunSummary } from "../types";

/**
 * A {@link RunSummary} decorated with its display run number. Oldest run gets
 * #1, newest run gets #N — independent of the order in which the backend
 * returned them.
 */
export interface NumberedRun extends RunSummary {
  runNumber: number;
}

/**
 * Assigns `Run #N` labels and returns the list sorted DESCENDING by
 * `startedAt` (newest first). The oldest run gets `runNumber = 1`; the newest
 * run gets `runNumber = N`. The returned array is in display order — newest
 * row at index 0, oldest at the last index — with `runNumber` descending in
 * step with `startedAt`.
 *
 * Determinism: equal `startedAt` values fall back to a stable ordinal compare
 * on `id` so the assigned numbers don't flip between renders. Equal
 * timestamps are unlikely in practice (each run inserts at the database
 * `UtcNow`) but two runs racing in the same millisecond should still produce
 * stable output.
 *
 * Pure function — no side effects, safe to call inside `useMemo`.
 */
export function assignRunNumbers(runs: RunSummary[]): NumberedRun[] {
  if (runs.length === 0) return [];
  // Sort DESCENDING by `startedAt` so the newest run sits at index 0 with
  // `runNumber = N`. Equal-timestamp pairs tiebreak on `id` (ascending) for
  // stable output across re-renders — a stray equal-millisecond race
  // shouldn't flip displayed run numbers.
  const sorted = [...runs].sort((a, b) => {
    const tDiff =
      new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
    return tDiff !== 0 ? tDiff : a.id.localeCompare(b.id);
  });
  return sorted.map((run, idx) => ({
    ...run,
    runNumber: sorted.length - idx,
  }));
}
